import { SidebarHeader } from "./SidebarHeader";
import { SidebarNavigation } from "./SidebarNavigation";
import { SidebarUserSection } from "./SidebarUserSection";
import { useSidebar } from "../hooks/useSidebar";
import type { NavItem, DropdownNavItem } from "../hooks/useNavState";
import type { User } from "@/domain/user";
import { getSidebarTheme, type SidebarVariant } from "./sidebarTheme";

interface Organization {
  uuid: string;
  name: string;
  logo_signed_url?: string;
}

interface UserOrganization {
  organization: Organization;
  role: string;
}

interface SidebarProps {
  user: User | null;
  showUserMenu: boolean;
  onToggleUserMenu: () => void;
  variant?: SidebarVariant;
  navItems?: NavItem[];
  dropdownItems?: DropdownNavItem[];
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onExpand?: () => void;
  organizationDetails?: Organization | null;
  currentOrganization?: { user_role?: string } | null;
  userOrganizations?: UserOrganization[] | null;
  onLogout?: () => void;
}

export const Sidebar = ({
  user,
  showUserMenu,
  onToggleUserMenu,
  variant = "default",
  navItems,
  dropdownItems,
  isCollapsed: externalIsCollapsed,
  onToggleCollapse: externalOnToggleCollapse,
  onExpand: externalOnExpand,
  organizationDetails,
  currentOrganization,
  userOrganizations,
  onLogout,
}: SidebarProps) => {
  const internalSidebar = useSidebar();
  const isCollapsed = externalIsCollapsed ?? internalSidebar.isCollapsed;
  const toggleCollapse = externalOnToggleCollapse ?? internalSidebar.toggleCollapse;
  const expand = externalOnExpand ?? internalSidebar.expand;

  const handleSidebarClick = () => {
    if (isCollapsed) {
      expand();
    }
  };

  const theme = getSidebarTheme(variant);

  const getSidebarClasses = () => {
    const widthClass = isCollapsed ? "w-sidebar-collapsed" : "w-sidebar-expanded";
    const baseClasses = `${widthClass} border-r flex flex-col transition-[width] duration-200 ease-out cursor-pointer fixed left-0 top-0 h-screen z-10`;

    return `${baseClasses} ${theme.background} ${theme.border}`;
  };

  return (
    <aside
      className={getSidebarClasses()}
      onClick={handleSidebarClick}
      onKeyDown={
        isCollapsed
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                expand();
              }
            }
          : undefined
      }
      tabIndex={isCollapsed ? 0 : undefined}
      role={isCollapsed ? "button" : undefined}
      aria-label={isCollapsed ? "Expand sidebar" : undefined}
    >
      <SidebarHeader isCollapsed={isCollapsed} onToggle={toggleCollapse} variant={variant} />
      <SidebarNavigation
        isCollapsed={isCollapsed}
        onExpand={expand}
        variant={variant}
        navItems={navItems}
        dropdownItems={dropdownItems}
      />
      <SidebarUserSection
        isCollapsed={isCollapsed}
        user={user}
        showMenu={showUserMenu}
        onToggleMenu={onToggleUserMenu}
        variant={variant}
        organizationDetails={organizationDetails}
        currentOrganization={currentOrganization}
        userOrganizations={userOrganizations}
        onLogout={onLogout}
      />
    </aside>
  );
};
